// src/pages/BetaChecklistFull.tsx
import  { useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, RotateCcw } from 'lucide-react';
import { usePDF } from 'react-to-pdf';
import mainlogo from '@/assets/mylistiq-logo-new.png'; 

import Navbar from '@/components/Navbar';
import CallToActionFooter from '@/components/Footer';



// ──────────────────────────────────────────────────────────────
// 1. Full Schema (all fields from your HTML)
const formSchema = z.object({
  full_name: z.string().min(1, 'Required'),
  date_completed: z.string().min(1, 'Required'),

  // Website
  web_branding: z.string().optional(),
  web_credibility: z.string().optional(),
  web_understanding: z.string().optional(),
  web_about: z.string().optional(),
  web_nav: z.string().optional(),
  web_speed: z.string().optional(),
  web_mobile: z.string().optional(),
  web_overall: z.string().optional(),
  feedback_website: z.string().optional(),

  // Pricing
  price_structure: z.string().optional(),
  price_features: z.string().optional(),
  price_monthly_annual: z.string().optional(),
  price_onetime: z.string().optional(),
  price_starter: z.string().optional(),
  price_pro: z.string().optional(),
  price_proplus: z.string().optional(),
  price_launches: z.string().optional(),
  price_hubs: z.string().optional(),
  price_rollover_pro: z.string().optional(),
  price_rollover_proplus: z.string().optional(),
  price_upgrade: z.string().optional(),
  price_cancel: z.string().optional(),
  price_checkout: z.string().optional(),
  price_overall: z.string().optional(),
  feedback_pricing: z.string().optional(),

  // Onboarding
  onb_signup: z.string().optional(),
  onb_fullname: z.string().optional(),
  onb_company: z.string().optional(),
  onb_profile: z.string().optional(),
  onb_photo: z.string().optional(),
  onb_logo: z.string().optional(),
  onb_facebook: z.string().optional(),
  onb_instagram: z.string().optional(),
  onb_overall: z.string().optional(),
  feedback_onboarding: z.string().optional(),

  // Dashboard
  dash_layout: z.string().optional(),
  dash_clarity: z.string().optional(),
  dash_smartstart: z.string().optional(),
  dash_month: z.string().optional(),
  dash_upgrade: z.string().optional(),
  dash_rollover: z.string().optional(),
  dash_ease: z.string().optional(),
  dash_visual: z.string().optional(),
  dash_overall: z.string().optional(),
  feedback_dashboard: z.string().optional(),

  // LaunchIQ Navigation
  liq_nav_steps: z.string().optional(),
  liq_nav_flow: z.string().optional(),
  liq_nav_types: z.string().optional(),
  liq_nav_purpose: z.string().optional(),
  liq_nav_overall: z.string().optional(),
  feedback_liq_nav: z.string().optional(),

  // Launch Creation & Data Entry
  liq_data_saved: z.string().optional(),
  liq_data_manual: z.string().optional(),
  liq_data_import: z.string().optional(),
  liq_data_correct: z.string().optional(),
  liq_data_overall: z.string().optional(),
  feedback_liq_data: z.string().optional(),

  // AutoIQ Content Generation
  liq_ai_desc: z.string().optional(),
  liq_ai_subject: z.string().optional(),
  liq_ai_quality: z.string().optional(),
  liq_ai_limit: z.string().optional(),
  liq_ai_overall: z.string().optional(),
  feedback_liq_ai: z.string().optional(),

  // Photos & Media
  liq_media_drag: z.string().optional(),
  liq_media_browse: z.string().optional(),
  liq_media_clear: z.string().optional(),
  liq_media_improve: z.string().optional(),
  liq_media_validate: z.string().optional(),
  liq_media_overall: z.string().optional(),
  feedback_liq_media: z.string().optional(),

  // Templates & Preview
  liq_tpl_selection: z.string().optional(),
  liq_tpl_edit: z.string().optional(),
  liq_tpl_features: z.string().optional(),
  liq_tpl_change: z.string().optional(),
  liq_tpl_match: z.string().optional(),
  liq_tpl_custom: z.string().optional(),
  liq_tpl_overall: z.string().optional(),
  feedback_liq_tpl: z.string().optional(),

  // Hub & Contact Integration
  liq_hub_add: z.string().optional(),
  liq_hub_contacts: z.string().optional(),
  liq_hub_overall: z.string().optional(),
  feedback_liq_hub: z.string().optional(),

  // Scheduling, AI & Send Options
  liq_send_ai_time: z.string().optional(),
  liq_send_now: z.string().optional(),
  liq_send_later: z.string().optional(),
  liq_send_recurring: z.string().optional(),
  liq_send_preds: z.string().optional(),
  liq_send_overall: z.string().optional(),
  feedback_liq_send: z.string().optional(),

  // Multi-Channel Distribution
  liq_dist_fb: z.string().optional(),
  liq_dist_ig: z.string().optional(),
  liq_dist_overall: z.string().optional(),
  feedback_liq_dist: z.string().optional(),

  // Notifications & Results
  liq_notif_confirm: z.string().optional(),
  liq_notif_sent: z.string().optional(),
  liq_notif_results: z.string().optional(),
  liq_notif_sms_sched: z.string().optional(),
  liq_notif_sms_results: z.string().optional(),
  liq_notif_overall: z.string().optional(),
  feedback_liq_notif: z.string().optional(),

  // LaunchIQ Management
  mgmt_list: z.string().optional(),
  mgmt_year: z.string().optional(),
  mgmt_status: z.string().optional(),
  mgmt_nav: z.string().optional(),
  mgmt_find: z.string().optional(),
  mgmt_overall: z.string().optional(),
  feedback_mgmt: z.string().optional(),

  // My Hub
  hub_clear: z.string().optional(),
  hub_manage: z.string().optional(),
  hub_group: z.string().optional(),
  hub_add: z.string().optional(),
  hub_remove: z.string().optional(),
  hub_update: z.string().optional(),
  hub_useful: z.string().optional(),
  hub_overall: z.string().optional(),
  feedback_hub: z.string().optional(),

  // Insights & Analytics
  ia_perf: z.string().optional(),
  ia_timing: z.string().optional(),
  ia_sent_over_time: z.string().optional(),
  ia_per_week: z.string().optional(),
  ia_clear: z.string().optional(),
  ia_layout: z.string().optional(),
  ia_overall: z.string().optional(),
  feedback_ia: z.string().optional(),

  // Overall Experience
  overall_learn: z.string().optional(),
  overall_value: z.string().optional(),
  overall_use: z.string().optional(),
  overall_recommend: z.string().optional(),
  feedback_final: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

// ──────────────────────────────────────────────────────────────
// Compact horizontal rating row component
const RatingRow = ({ name, label }: { name: keyof FormData; label: string }) => {
  const { register, watch } = useFormContext<FormData>();
  const value = watch(name);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center py-3 border-t border-gray-200 first:border-t-0">
      <div className="font-medium text-gray-800">{label}</div>

      <div className="flex items-center gap-2 sm:gap-3">
        {[1, 2, 3, 4, 5].map((val) => (
          <label key={val} className="cursor-pointer select-none">
            <input
              type="radio"
              value={val}
              {...register(name)}
              className="sr-only peer"
            />
            <div
              className={`
                w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold transition-all
                ${
                  value === val.toString()
                    ? 'bg-red-600 text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
                }
              `}
            >
              {val}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// Main Component
const BetaChecklistFull: React.FC = () => {
  
  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date_completed: new Date().toISOString().split('T')[0],
    },
  });

  const { watch, reset } = methods;
  const formData = watch();

  const { toPDF, targetRef } = usePDF({
    filename: `MyListIQ_Beta_Checklist_${formData.full_name || 'Tester'}_${new Date().toISOString().slice(0, 10)}.pdf`,
  });


  const [isGenerating, setIsGenerating] = useState(false);

    const handleDownloadPDF = async () => {
      setIsGenerating(true);

      // allow DOM to update before capture
      setTimeout(async () => {
        await toPDF();
        setIsGenerating(false);
      }, 100);
    };

  return (
    <FormProvider {...methods}>
      <Navbar />

      <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto space-y-10">

          {/* Instructions */}
          <Alert className="bg-blue-50 border-l-4 border-blue-600">
            <AlertDescription className="text-base space-y-2">
              <strong className="text-lg block">Instructions</strong>
              <ol className="list-decimal pl-5 space-y-1 text-gray-700">
                <li>Complete the checklist online</li>
                <li>Click "Download Completed Checklist (PDF)" when finished</li>
                <li>Email the PDF to <span className="font-bold text-red-600">contracts@mylistiq.com</span></li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Printable content */}
          <div
            ref={targetRef}
            className="bg-white rounded-xl shadow-lg p-8 lg:p-12 space-y-12 print:shadow-none print:p-6"
          >
            {/* Header */}
            <header className="flex flex-col sm:flex-row justify-between items-start gap-8 pb-8 border-b">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  MyListIQ Beta Tester Checklist & Feature Rating Form
                </h1>
                <p className="mt-3 text-gray-600">
                  Thank you for participating in the MyListIQ beta program! Your feedback is truly valued.
                </p>
                <div className="mt-4 inline-block bg-gray-100 px-4 py-2 rounded-full text-sm">
                  <strong>Rating Scale:</strong> 1 = Not Helpful | 5 = Excellent
                </div>
              </div>
              <img src={mainlogo} alt="MyListIQ" className="h-16 w-auto" />
            </header>

            {/* Tester Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-lg">
              <div><strong>Full Name:</strong> {formData.full_name || '________________'}</div>
              <div><strong>Date:</strong> {formData.date_completed || '________________'}</div>
            </div>

            {/* ────────────── All Sections ────────────── */}

            {/* Website */}
            <section>
              <h2 className="text-2xl font-bold mb-6">MyListIQ Website (Overall Experience & Usability)</h2>
              <RatingRow name="web_branding" label="Website visual design & branding" />
              <RatingRow name="web_credibility" label="Professional look and credibility" />
              <RatingRow name="web_understanding" label="Ease of understanding what MyListIQ does" />
              <RatingRow name="web_about" label="Does the “About” provide an overview of the company and offering" />
              <RatingRow name="web_nav" label="Website navigation & menu clarity" />
              <RatingRow name="web_speed" label="Page load speed & responsiveness" />
              <RatingRow name="web_mobile" label="Mobile / tablet experience (if tested)" />
              <RatingRow name="web_overall" label="Overall experience of site and functionality" />

             <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Pricing */}
            <section>
              <h2 className="text-2xl font-bold mb-6">MyListIQ Website – Pricing (One-Time & Subscription Plans)</h2>
              <RatingRow name="price_structure" label="Pricing structure (One-Time vs Subscription)" />
              <RatingRow name="price_features" label="Pricing features per plan" />
              <RatingRow name="price_monthly_annual" label="Option for Monthly and Annual subscriptions" />
              <RatingRow name="price_onetime" label="Pricing for One-Time plan" />
              <RatingRow name="price_starter" label="Pricing for Starter plan" />
              <RatingRow name="price_pro" label="Pricing for Pro plan" />
              <RatingRow name="price_proplus" label="Pricing for Pro+ plan" />
              <RatingRow name="price_launches" label="Number of Launches included per plan" />
              <RatingRow name="price_hubs" label="Number of Hubs included per plan" />
              <RatingRow name="price_rollover_pro" label="Rollover option for Pro (up to 3 launches/year)" />
              <RatingRow name="price_rollover_proplus" label="Rollover option for Pro+ (up to 10 launches/year)" />
              <RatingRow name="price_upgrade" label="Ability to upgrade plan" />
              <RatingRow name="price_cancel" label="Ability to cancel subscription" />
              <RatingRow name="price_checkout" label="Payment / checkout experience" />
              <RatingRow name="price_overall" label="Overall pricing experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Onboarding */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Onboarding / Account Creation</h2>
              <RatingRow name="onb_signup" label="Account sign-up process was easy to follow" />
              <RatingRow name="onb_fullname" label="Entering full name" />
              <RatingRow name="onb_company" label="Entering company name" />
              <RatingRow name="onb_profile" label="Updating profile information" />
              <RatingRow name="onb_photo" label="Adding profile photo" />
              <RatingRow name="onb_logo" label="Adding company logo" />
              <RatingRow name="onb_facebook" label="Connecting to Facebook" />
              <RatingRow name="onb_instagram" label="Connecting to Instagram" />
              <RatingRow name="onb_overall" label="Overall onboarding & account setup experience" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Dashboard */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
              <RatingRow name="dash_layout" label="Dashboard layout & organization" />
              <RatingRow name="dash_clarity" label="Clarity of dashboard labels & sections" />
              <RatingRow name="dash_smartstart" label="Smart Start steps were easy to follow" />
              <RatingRow name="dash_month" label="This Month at a Glance (30-day view)" />
              <RatingRow name="dash_upgrade" label="Easy to view or upgrade my plan" />
              <RatingRow name="dash_rollover" label="Easy to track remaining launches and rollovers" />
              <RatingRow name="dash_ease" label="Overall ease of use" />
              <RatingRow name="dash_visual" label="Visual clarity (not cluttered)" />
              <RatingRow name="dash_overall" label="Overall dashboard experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* LaunchIQ Navigation */}
            <section>
              <h2 className="text-2xl font-bold mb-6">LaunchIQ Builder → Navigation</h2>
              <RatingRow name="liq_nav_steps" label="Step-by-step navigation was clear" />
              <RatingRow name="liq_nav_flow" label="Overall workflow made sense" />
              <RatingRow name="liq_nav_types" label="Launch types (Property vs Service) were clear" />
              <RatingRow name="liq_nav_purpose" label="Purpose of each step was clear" />
              <RatingRow name="liq_nav_overall" label="Overall navigation experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Launch Creation & Data Entry */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Launch Creation & Data Entry</h2>
              <RatingRow name="liq_data_saved" label="Launch was saved successfully" />
              <RatingRow name="liq_data_manual" label="Manual entry of launch information" />
              <RatingRow name="liq_data_import" label="Import from county records" />
              <RatingRow name="liq_data_correct" label="Property data imported correctly" />
              <RatingRow name="liq_data_overall" label="Overall launch creation experience" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* AutoIQ Content Generation */}
            <section>
              <h2 className="text-2xl font-bold mb-6">AutoIQ Content Generation</h2>
              <RatingRow name="liq_ai_desc" label="AutoIQ revised the description effectively" />
              <RatingRow name="liq_ai_subject" label="AutoIQ generated a catchy subject line" />
              <RatingRow name="liq_ai_quality" label="Overall quality of AutoIQ-generated content" />
              <RatingRow name="liq_ai_limit" label="AutoIQ limit of 3 was clear" />
              <RatingRow name="liq_ai_overall" label="Overall AutoIQ experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Photos & Media */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Photos & Media</h2>
              <RatingRow name="liq_media_drag" label="Drag-and-drop photo upload worked" />
              <RatingRow name="liq_media_browse" label="Browse-for-photo upload option worked" />
              <RatingRow name="liq_media_clear" label="AutoIQ photo enhancement feature was clear" />
              <RatingRow name="liq_media_improve" label="AutoIQ photo enhancement improved the photo" />
              <RatingRow name="liq_media_validate" label="Validation status was helpful" />
              <RatingRow name="liq_media_overall" label="Overall photos & media experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Templates & Preview */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Templates & Preview</h2>
              <RatingRow name="liq_tpl_selection" label="Template selection was sufficient" />
              <RatingRow name="liq_tpl_edit" label="Able to edit launch preview" />
              <RatingRow name="liq_tpl_features" label="Able to add additional features" />
              <RatingRow name="liq_tpl_change" label="Able to change template" />
              <RatingRow name="liq_tpl_match" label="Preview matched email proof" />
              <RatingRow name="liq_tpl_custom" label="Fully customizable templates would be beneficial" />
              <RatingRow name="liq_tpl_overall" label="Overall templates & preview experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Hub & Contact Integration */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Hub & Contact Integration</h2>
              <RatingRow name="liq_hub_add" label="Able to add Beta Hub to launch" />
              <RatingRow name="liq_hub_contacts" label="Able to add contact(s) from My Hub" />
              <RatingRow name="liq_hub_overall" label="Overall Hub & contact experience and functionality" />

                           <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Scheduling, AI & Send Options */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Scheduling, AI & Send Options</h2>
              <RatingRow name="liq_send_ai_time" label="AI-recommended send time was useful" />
              <RatingRow name="liq_send_now" label="Send-now option worked" />
              <RatingRow name="liq_send_later" label="Schedule-for-later option worked" />
              <RatingRow name="liq_send_recurring" label="Recurring launch scheduling" />
              <RatingRow name="liq_send_preds" label="AI predictions by Hub were useful" />
              <RatingRow name="liq_send_overall" label="Overall scheduling, AI & send experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Multi-Channel Distribution */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Multi-Channel Distribution</h2>
              <RatingRow name="liq_dist_fb" label="Facebook distribution worked" />
              <RatingRow name="liq_dist_ig" label="Instagram distribution worked" />
              <RatingRow name="liq_dist_overall" label="Overall distribution experience and functionality" />

                           <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Notifications & Results */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Notifications & Results</h2>
              <RatingRow name="liq_notif_confirm" label="Email confirming successful launch" />
              <RatingRow name="liq_notif_sent" label="Received email launch sent" />
              <RatingRow name="liq_notif_results" label="Email with launch results" />
              <RatingRow name="liq_notif_sms_sched" label="SMS for scheduled launch" />
              <RatingRow name="liq_notif_sms_results" label="SMS with launch results" />
              <RatingRow name="liq_notif_overall" label="Overall notifications & results experience and functionality" />

                         <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* LaunchIQ Management */}
            <section>
              <h2 className="text-2xl font-bold mb-6">LaunchIQ Management</h2>
              <RatingRow name="mgmt_list" label="Viewing an overall list of launches" />
              <RatingRow name="mgmt_year" label="Ability to view all launches for the year" />
              <RatingRow name="mgmt_status" label="Clarity of launch status" />
              <RatingRow name="mgmt_nav" label="Ease of navigating between launches" />
              <RatingRow name="mgmt_find" label="Ability to locate a specific launch" />
              <RatingRow name="mgmt_overall" label="Overall LaunchIQ Management experience and functionality" />

                         <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* My Hub */}
            <section>
              <h2 className="text-2xl font-bold mb-6">My Hub</h2>
              <RatingRow name="hub_clear" label="How My Hub works with your launch was clear" />
              <RatingRow name="hub_manage" label="Managing My Hub" />
              <RatingRow name="hub_group" label="Able to create a group" />
              <RatingRow name="hub_add" label="Able to add contact(s) to a group" />
              <RatingRow name="hub_remove" label="Able to remove contact(s) from a group" />
              <RatingRow name="hub_update" label="Able to update contact(s) information" />
              <RatingRow name="hub_useful" label="Overall usefulness of My Hub" />
              <RatingRow name="hub_overall" label="Overall My Hub experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Insights & Analytics */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Insights & Analytics</h2>
              <RatingRow name="ia_perf" label="Launch performance" />
              <RatingRow name="ia_timing" label="Sending timing – clicks by time & date" />
              <RatingRow name="ia_sent_over_time" label="Email sent over time" />
              <RatingRow name="ia_per_week" label="Launches per week" />
              <RatingRow name="ia_clear" label="Analytics & insights were clear and easy to follow" />
              <RatingRow name="ia_layout" label="Layout and organization of data" />
              <RatingRow name="ia_overall" label="Overall insights & analytics experience and functionality" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            {/* Overall Experience */}
            <section>
              <h2 className="text-2xl font-bold mb-6">Overall Experience</h2>
              <RatingRow name="overall_learn" label="Ease of learning MyListIQ" />
              <RatingRow name="overall_value" label="Overall platform value" />
              <RatingRow name="overall_use" label="Likelihood to use after launch" />
              <RatingRow name="overall_recommend" label="Likelihood to recommend" />

                          <div className="mt-6">
                <strong>Feedback:</strong>
                <Textarea
                  {...methods.register('feedback_website')}
                  className="mt-2 bg-gray-50"
                  rows={4}
                  placeholder="Enter your feedback here..."
                />
              </div>
            </section>

            <footer className="text-center text-sm text-gray-500 pt-10 border-t mt-10">
              Beta v1.0 • © MyListIQ • Confidential – For Testing Purposes Only
            </footer>
          </div>

          {/* Form - real inputs */}
          <div className="bg-white rounded-xl shadow-lg p-8 space-y-10">
            <h2 className="text-3xl font-bold text-center">Complete Your Checklist</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Full Name</Label>
                <Input {...methods.register('full_name')} className="mt-1" />
              </div>
              <div>
                <Label>Date Completed</Label>
                <Input type="date" {...methods.register('date_completed')} className="mt-1" />
              </div>
            </div>

            {/* All RatingRows and Textareas */}
            {/* Website */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Website</h3>
              <RatingRow name="web_branding" label="Website visual design & branding" />
              <RatingRow name="web_credibility" label="Professional look and credibility" />
              <RatingRow name="web_understanding" label="Ease of understanding what MyListIQ does" />
              <RatingRow name="web_about" label="Does the “About” provide an overview of the company and offering" />
              <RatingRow name="web_nav" label="Website navigation & menu clarity" />
              <RatingRow name="web_speed" label="Page load speed & responsiveness" />
              <RatingRow name="web_mobile" label="Mobile / tablet experience (if tested)" />
              <RatingRow name="web_overall" label="Overall experience of site and functionality" />
              <Textarea {...methods.register('feedback_website')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Pricing */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Pricing</h3>
              <RatingRow name="price_structure" label="Pricing structure (One-Time vs Subscription)" />
              <RatingRow name="price_features" label="Pricing features per plan" />
              <RatingRow name="price_monthly_annual" label="Option for Monthly and Annual subscriptions" />
              <RatingRow name="price_onetime" label="Pricing for One-Time plan" />
              <RatingRow name="price_starter" label="Pricing for Starter plan" />
              <RatingRow name="price_pro" label="Pricing for Pro plan" />
              <RatingRow name="price_proplus" label="Pricing for Pro+ plan" />
              <RatingRow name="price_launches" label="Number of Launches included per plan" />
              <RatingRow name="price_hubs" label="Number of Hubs included per plan" />
              <RatingRow name="price_rollover_pro" label="Rollover option for Pro (up to 3 launches/year)" />
              <RatingRow name="price_rollover_proplus" label="Rollover option for Pro+ (up to 10 launches/year)" />
              <RatingRow name="price_upgrade" label="Ability to upgrade plan" />
              <RatingRow name="price_cancel" label="Ability to cancel subscription" />
              <RatingRow name="price_checkout" label="Payment / checkout experience" />
              <RatingRow name="price_overall" label="Overall pricing experience and functionality" />
              <Textarea {...methods.register('feedback_pricing')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Onboarding */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Onboarding</h3>
              <RatingRow name="onb_signup" label="Account sign-up process was easy to follow" />
              <RatingRow name="onb_fullname" label="Entering full name" />
              <RatingRow name="onb_company" label="Entering company name" />
              <RatingRow name="onb_profile" label="Updating profile information" />
              <RatingRow name="onb_photo" label="Adding profile photo" />
              <RatingRow name="onb_logo" label="Adding company logo" />
              <RatingRow name="onb_facebook" label="Connecting to Facebook" />
              <RatingRow name="onb_instagram" label="Connecting to Instagram" />
              <RatingRow name="onb_overall" label="Overall onboarding & account setup experience" />
              <Textarea {...methods.register('feedback_onboarding')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Dashboard */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Dashboard</h3>
              <RatingRow name="dash_layout" label="Dashboard layout & organization" />
              <RatingRow name="dash_clarity" label="Clarity of dashboard labels & sections" />
              <RatingRow name="dash_smartstart" label="Smart Start steps were easy to follow" />
              <RatingRow name="dash_month" label="This Month at a Glance (30-day view)" />
              <RatingRow name="dash_upgrade" label="Easy to view or upgrade my plan" />
              <RatingRow name="dash_rollover" label="Easy to track remaining launches and rollovers" />
              <RatingRow name="dash_ease" label="Overall ease of use" />
              <RatingRow name="dash_visual" label="Visual clarity (not cluttered)" />
              <RatingRow name="dash_overall" label="Overall dashboard experience and functionality" />
              <Textarea {...methods.register('feedback_dashboard')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* LaunchIQ Navigation */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">LaunchIQ Navigation</h3>
              <RatingRow name="liq_nav_steps" label="Step-by-step navigation was clear" />
              <RatingRow name="liq_nav_flow" label="Overall workflow made sense" />
              <RatingRow name="liq_nav_types" label="Launch types (Property vs Service) were clear" />
              <RatingRow name="liq_nav_purpose" label="Purpose of each step was clear" />
              <RatingRow name="liq_nav_overall" label="Overall navigation experience and functionality" />
              <Textarea {...methods.register('feedback_liq_nav')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Launch Creation & Data Entry */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Launch Creation & Data Entry</h3>
              <RatingRow name="liq_data_saved" label="Launch was saved successfully" />
              <RatingRow name="liq_data_manual" label="Manual entry of launch information" />
              <RatingRow name="liq_data_import" label="Import from county records" />
              <RatingRow name="liq_data_correct" label="Property data imported correctly" />
              <RatingRow name="liq_data_overall" label="Overall launch creation experience" />
              <Textarea {...methods.register('feedback_liq_data')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* AutoIQ Content Generation */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">AutoIQ Content Generation</h3>
              <RatingRow name="liq_ai_desc" label="AutoIQ revised the description effectively" />
              <RatingRow name="liq_ai_subject" label="AutoIQ generated a catchy subject line" />
              <RatingRow name="liq_ai_quality" label="Overall quality of AutoIQ-generated content" />
              <RatingRow name="liq_ai_limit" label="AutoIQ limit of 3 was clear" />
              <RatingRow name="liq_ai_overall" label="Overall AutoIQ experience and functionality" />
              <Textarea {...methods.register('feedback_liq_ai')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Photos & Media */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Photos & Media</h3>
              <RatingRow name="liq_media_drag" label="Drag-and-drop photo upload worked" />
              <RatingRow name="liq_media_browse" label="Browse-for-photo upload option worked" />
              <RatingRow name="liq_media_clear" label="AutoIQ photo enhancement feature was clear" />
              <RatingRow name="liq_media_improve" label="AutoIQ photo enhancement improved the photo" />
              <RatingRow name="liq_media_validate" label="Validation status was helpful" />
              <RatingRow name="liq_media_overall" label="Overall photos & media experience and functionality" />
              <Textarea {...methods.register('feedback_liq_media')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Templates & Preview */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Templates & Preview</h3>
              <RatingRow name="liq_tpl_selection" label="Template selection was sufficient" />
              <RatingRow name="liq_tpl_edit" label="Able to edit launch preview" />
              <RatingRow name="liq_tpl_features" label="Able to add additional features" />
              <RatingRow name="liq_tpl_change" label="Able to change template" />
              <RatingRow name="liq_tpl_match" label="Preview matched email proof" />
              <RatingRow name="liq_tpl_custom" label="Fully customizable templates would be beneficial" />
              <RatingRow name="liq_tpl_overall" label="Overall templates & preview experience and functionality" />
              <Textarea {...methods.register('feedback_liq_tpl')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Hub & Contact Integration */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Hub & Contact Integration</h3>
              <RatingRow name="liq_hub_add" label="Able to add Beta Hub to launch" />
              <RatingRow name="liq_hub_contacts" label="Able to add contact(s) from My Hub" />
              <RatingRow name="liq_hub_overall" label="Overall Hub & contact experience and functionality" />
              <Textarea {...methods.register('feedback_liq_hub')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Scheduling, AI & Send Options */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Scheduling, AI & Send Options</h3>
              <RatingRow name="liq_send_ai_time" label="AI-recommended send time was useful" />
              <RatingRow name="liq_send_now" label="Send-now option worked" />
              <RatingRow name="liq_send_later" label="Schedule-for-later option worked" />
              <RatingRow name="liq_send_recurring" label="Recurring launch scheduling" />
              <RatingRow name="liq_send_preds" label="AI predictions by Hub were useful" />
              <RatingRow name="liq_send_overall" label="Overall scheduling, AI & send experience and functionality" />
              <Textarea {...methods.register('feedback_liq_send')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Multi-Channel Distribution */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Multi-Channel Distribution</h3>
              <RatingRow name="liq_dist_fb" label="Facebook distribution worked" />
              <RatingRow name="liq_dist_ig" label="Instagram distribution worked" />
              <RatingRow name="liq_dist_overall" label="Overall distribution experience and functionality" />
              <Textarea {...methods.register('feedback_liq_dist')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Notifications & Results */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Notifications & Results</h3>
              <RatingRow name="liq_notif_confirm" label="Email confirming successful launch" />
              <RatingRow name="liq_notif_sent" label="Received email launch sent" />
              <RatingRow name="liq_notif_results" label="Email with launch results" />
              <RatingRow name="liq_notif_sms_sched" label="SMS for scheduled launch" />
              <RatingRow name="liq_notif_sms_results" label="SMS with launch results" />
              <RatingRow name="liq_notif_overall" label="Overall notifications & results experience and functionality" />
              <Textarea {...methods.register('feedback_liq_notif')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* LaunchIQ Management */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">LaunchIQ Management</h3>
              <RatingRow name="mgmt_list" label="Viewing an overall list of launches" />
              <RatingRow name="mgmt_year" label="Ability to view all launches for the year" />
              <RatingRow name="mgmt_status" label="Clarity of launch status" />
              <RatingRow name="mgmt_nav" label="Ease of navigating between launches" />
              <RatingRow name="mgmt_find" label="Ability to locate a specific launch" />
              <RatingRow name="mgmt_overall" label="Overall LaunchIQ Management experience and functionality" />
              <Textarea {...methods.register('feedback_mgmt')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* My Hub */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">My Hub</h3>
              <RatingRow name="hub_clear" label="How My Hub works with your launch was clear" />
              <RatingRow name="hub_manage" label="Managing My Hub" />
              <RatingRow name="hub_group" label="Able to create a group" />
              <RatingRow name="hub_add" label="Able to add contact(s) to a group" />
              <RatingRow name="hub_remove" label="Able to remove contact(s) from a group" />
              <RatingRow name="hub_update" label="Able to update contact(s) information" />
              <RatingRow name="hub_useful" label="Overall usefulness of My Hub" />
              <RatingRow name="hub_overall" label="Overall My Hub experience and functionality" />
              <Textarea {...methods.register('feedback_hub')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Insights & Analytics */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Insights & Analytics</h3>
              <RatingRow name="ia_perf" label="Launch performance" />
              <RatingRow name="ia_timing" label="Sending timing – clicks by time & date" />
              <RatingRow name="ia_sent_over_time" label="Email sent over time" />
              <RatingRow name="ia_per_week" label="Launches per week" />
              <RatingRow name="ia_clear" label="Analytics & insights were clear and easy to follow" />
              <RatingRow name="ia_layout" label="Layout and organization of data" />
              <RatingRow name="ia_overall" label="Overall insights & analytics experience and functionality" />
              <Textarea {...methods.register('feedback_ia')} placeholder="Your feedback..." rows={4} />
            </div>

            {/* Overall Experience */}
            <div className="space-y-8">
              <h3 className="text-xl font-semibold">Overall Experience</h3>
              <RatingRow name="overall_learn" label="Ease of learning MyListIQ" />
              <RatingRow name="overall_value" label="Overall platform value" />
              <RatingRow name="overall_use" label="Likelihood to use after launch" />
              <RatingRow name="overall_recommend" label="Likelihood to recommend" />
              <Textarea {...methods.register('feedback_final')} placeholder="Your final feedback..." rows={4} />
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-8">
              <Button
  type="button"
  onClick={handleDownloadPDF}
  disabled={isGenerating}
  className="bg-red-600 hover:bg-red-700 text-white px-10"
>
  {isGenerating ? (
    <>
      <svg className="animate-spin mr-2 h-5 w-5" viewBox="0 0 24 24" />
      Generating PDF...
    </>
  ) : (
    <>
      <Download className="mr-2 h-5 w-5" />
      Download as PDF
    </>
  )}
</Button>

              <Button variant="outline" onClick={() => reset()}>
                <RotateCcw className="mr-2 h-5 w-5" />
                Reset All
              </Button>
            </div>
          </div>
        </div>
      </div>

      <CallToActionFooter />
    </FormProvider>
  );
};

export default BetaChecklistFull;